// Deploy field report 2026-07-13 — `type: nextjs` build-script preflight.
//
// The CloudGrid builder deploys a `type: nextjs` service by running its
// `scripts.build` and expecting standard Next.js output. Projects scaffolded
// for other platforms often point `scripts.build` at a wrapper CLI instead —
// the remote build then fails after minutes of Cloud Build time. This module
// detects the mismatch client-side so `grid plug` can abort before the
// tarball upload.
//
// Careful case: a wrapper command can CONTAIN the substring "next". The
// check matches `next` as a command token (split on shell separators),
// never as a substring.

export interface NextjsBuildScriptFinding {
  service: string;
  script: string;
}

/** Split a shell command into command-ish tokens (drops env assignments). */
function commandTokens(script: string): string[] {
  return script
    .split(/[\s;&|()]+/)
    .filter((t) => t.length > 0 && !/^[A-Za-z_][A-Za-z0-9_]*=/.test(t));
}

/**
 * True when the script invokes `next` as its own command token —
 * `next build`, `npx next build`, `node_modules/.bin/next build` all pass;
 * a wrapper whose name merely contains "next" does not.
 */
export function invokesNextCli(script: string): boolean {
  return commandTokens(script).some(
    (t) => t === 'next' || t.endsWith('/next'),
  );
}

/**
 * Check one nextjs service's `scripts.build`. Returns a finding when the
 * script exists and does not invoke the `next` CLI; null when it's fine
 * (or absent — Next's default build applies then).
 */
export function checkNextjsBuildScript(
  service: string,
  buildScript: string | undefined,
): NextjsBuildScriptFinding | null {
  if (buildScript === undefined || buildScript.trim().length === 0) {
    return null;
  }
  if (invokesNextCli(buildScript)) return null;
  return { service, script: buildScript };
}

/** §23-voiced error message for a finding. */
export function nextjsBuildScriptMessage(
  finding: NextjsBuildScriptFinding,
): string {
  return (
    `Service ${finding.service} is type nextjs, but scripts.build runs ` +
    `'${finding.script}' instead of 'next build'. The CloudGrid builder ` +
    'runs your build script, so this deploy will fail. ' +
    "Point scripts.build at 'next build' (move the current command to " +
    'another script name), or change the service type. ' +
    'Re-run with --skip-preflight to deploy anyway.'
  );
}
